<template>
  <section class="splash" :class="classnames">
    <div class="splash__logo-wrap">
      <div class="splash__logo">
        <picture>
          <source type="image/webp" srcset="@/assets/logo.webp" />
          <img
            class="logo"
            :class="classnames"
            src="@/assets/logo.png"
            alt=""
          />
        </picture>
      </div>
    </div>

    <div class="layer" :class="classnames"></div>
    <svg
      width="1440"
      height="974"
      viewBox="0 0 1440 974"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      :class="classnames"
    >
      <defs>
        <mask id="splashMask">
          <rect
            class="mask-rect"
            :class="classnames"
            fill="white"
            :x="$store.state.isMobile ? 360 : 0"
            y="-233"
            width="1440"
            height="1440"
          />
        </mask>
      </defs>

      <g mask="url(#splashMask)">
        <path
          d="M1498 0H641L67 359.5H252.5L-85.5 628L-56.5 984H877L1578 80L1323.5 221.5L1498 0Z"
          fill="white"
        />
        <g filter="url(#splash-shadow)">
          <path
            class="tri tri01"
            d="M441.741 -140.13L99.7412 257.87L981.741 -140.13L441.741 -140.13Z"
            fill="white"
            opacity="0.3"
          />
          <path
            class="tri tri02"
            d="M1207.34 633L675.339 1165L1044.5 1165L1207.34 633Z"
            fill="white"
            opacity="0.3"
          />
          <path
            class="tri tri03"
            d="M294.5 663L-405 973L68 973L201.5 828L125.5 849.5L294.5 663Z"
            fill="#E40718"
            opacity="0.3"
          />
        </g>
        <path
          class="tri tri01"
          d="M441.741 -140.13L99.7412 257.87L981.741 -140.13L441.741 -140.13Z"
          fill="white"
        />
        <path
          class="tri tri02"
          d="M1207.34 633L675.339 1165L1044.5 1165L1207.34 633Z"
          fill="white"
        />
        <path
          class="tri tri03"
          d="M294.5 663L-405 973L68 973L201.5 828L125.5 849.5L294.5 663Z"
          fill="#E40718"
        />
      </g>

      <filter id="splash-shadow">
        <feGaussianBlur in="SourceAlpha" stdDeviation="8"></feGaussianBlur>
        <feOffset dx="8" dy="8" />
      </filter>
    </svg>
  </section>
</template>

<script>
import imagesLoaded from 'imagesloaded'
import gsap from 'gsap'
import { wait } from '@/modules/utils'

export default {
  data() {
    return {
      state: 'is-loading'
    }
  },

  computed: {
    classnames() {
      return {
        'is-enter': this.state === 'enter',
        'is-leave': this.state === 'leave',
        'is-ready': this.state === 'afterLeave'
      }
    }
  },

  async mounted() {
    const { commit } = this.$store
    const splashImgLoad = new imagesLoaded(this.$el)

    await splashImgLoad.on('always', async (inst) => {
      await wait(100)
      commit('startTransition')
      this.onEnter()
    })
  },

  methods: {
    async onEnter() {
      const { commit } = this.$store

      await gsap.to(this.$el, {
        opacity: 1,
        duration: 0.4
      })

      this.state = 'enter'
      await wait(2200)

      commit('endTransition')
      // 既にロード完了していたらそのままleaveアクションを実行
      if (this.$store.state.isLoaded) {
        this.onLeave()
      }
    },
    async onLeave() {
      const { commit } = this.$store
      this.state = 'leave'
      await wait(1200)
      this.state = 'afterLeave'
      commit('showView')
      gsap.to(this.$el, {
        opacity: 0,
        duration: 0.5
      })
    }
  },
  watch: {
    '$store.state.isTransition'(isTransition) {
      isTransition && this.onEnter()
    },

    '$store.state.isLoaded'(isLoaded) {
      if (isLoaded && !this.$store.state.isTransition) {
        this.onLeave()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$transition-delay: 1s;

.splash {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  opacity: 1;
  background-color: var(--main-color);
  &.is-ready {
    pointer-events: none;
  }

  &__logo-wrap {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9;
  }

  &__logo {
    .logo {
      width: 42vw;
      opacity: 0;
      transform: translate(350px, 0px) scale(1.5, 0.8);
      @media screen and (max-width: $breakpoint-mobile) {
        width: 80vw;
      }

      @keyframes logoEnter {
        0% {
          opacity: 0;
          transform: translate(350px, -100px) scale(1.5, 0.8);
        }
        100% {
          opacity: 1;
          transform: translate(0px, 0px) scale(1, 1);
        }
      }

      @keyframes logoLeave {
        0% {
          opacity: 1;
          transform: translate(0px, 0px) scale(1, 1);
        }

        80% {
          opacity: 0;
        }

        100% {
          opacity: 0;
          transform: translate(-450px, 100px) scale(1.5, 0.8)
            skew(-10deg, -10deg);
        }
      }

      &.is-enter {
        will-change: transform;
        animation: logoEnter 0.75s 1s $ease forwards;
      }

      &.is-leave {
        will-change: transform;
        animation: logoLeave 0.8s $ease both;
      }
    }
  }

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 120%;
    height: 100%;
    z-index: 9;

    @keyframes splashEnter {
      0% {
        transform: translate(0%, 0) skew(0deg, 0deg);
      }
      100% {
        transform: translate(-130%, 0) skew(-20deg, 0deg);
      }
    }

    @keyframes splashLeave {
      0% {
        transform: translate(130%, 0) skew(-20deg, 0deg);
      }

      100% {
        transform: translate(0%, 0) skew(0deg, 0deg);
      }
    }

    &:before {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      background-color: var(--main-color);
      transform: translate(0%, 0) skew(0deg, 0deg);
      z-index: 9;
      will-change: transform;
    }

    &.is-enter {
      &:before {
        will-change: transform;
        animation: splashEnter 1.4s $ease forwards;
      }
    }

    &.is-leave {
      &:before {
        will-change: transform;
        animation: splashLeave 1.2s $ease both;
      }
    }
  }

  svg {
    width: 100%;
    height: 100vh;

    .mask-rect {
      transform-origin: center !important;
      will-change: transform;
      transform: translate(0%, 0px) skew(0deg, 0deg);
      will-change: transform;
    }

    .tri01 {
      &.is-enter {
        transform: translate3d(-20%, 14%, 0);
      }
    }

    .tri02 {
      &.is-enter {
        transform: translate3d(20%, -14%, 0);
      }
    }

    .tri03 {
      &.is-enter {
        transform: translate3d(-5%, 5%, 0);
      }
    }
  }
}
</style>
